import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

function Social() {
  const [users, setUsers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const isDevMode = localStorage.getItem('devMode') === 'true';
  const currentUserId = isDevMode ? 'dev-user' : (currentUser?.uid || 'unknown');

  useEffect(() => {
    if (!currentUser && !isDevMode) {
      navigate('/');
      return;
    }

    loadUsers();
    loadAddedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate, isDevMode]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      if (!db || currentUserId === 'unknown') {
        setLoading(false);
        return;
      }

      // Load all users with slopes or epic friend links
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = [];
      
      usersSnapshot.forEach((docSnapshot) => {
        const userData = docSnapshot.data();
        const userId = docSnapshot.id;
        
        // Skip current user
        if (userId === currentUserId) return;
        
        // Only include users with at least one friend link
        if (userData.slopesFriendLink || userData.epicFriendLink) {
          usersList.push({
            id: userId,
            displayName: userData.displayName || userData.originalName || 'Unknown',
            originalName: userData.originalName,
            slopesFriendLink: userData.slopesFriendLink || '',
            epicFriendLink: userData.epicFriendLink || ''
          });
        }
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAddedUsers = async () => {
    try {
      if (!db || currentUserId === 'unknown') return;

      // Load friend connections for current user
      const connectionsRef = collection(db, 'friendConnections');
      const q = query(connectionsRef, where('addedBy', '==', currentUserId));
      const snapshot = await getDocs(q);
      
      const addedIds = [];
      snapshot.forEach((doc) => {
        addedIds.push(doc.data().addedUserId);
      });

      setAddedUsers(addedIds);
    } catch (error) {
      console.error('Error loading added users:', error);
    }
  };

  const handleMarkAsAdded = async (userId) => {
    try {
      if (!db || currentUserId === 'unknown') {
        setError('Cannot save connection. Please ensure you are logged in.');
        return;
      }

      // Create connection document
      const connectionId = `${currentUserId}_${userId}`;
      const connectionRef = doc(db, 'friendConnections', connectionId);
      
      await setDoc(connectionRef, {
        addedBy: currentUserId,
        addedUserId: userId,
        createdAt: new Date().toISOString()
      });

      // Update local state
      setAddedUsers([...addedUsers, userId]);
    } catch (error) {
      console.error('Error marking as added:', error);
      setError('Failed to save connection. Please try again.');
    }
  };

  const handleRemoveAdded = async (userId) => {
    try {
      if (!db || currentUserId === 'unknown') return;

      const connectionId = `${currentUserId}_${userId}`;
      const connectionRef = doc(db, 'friendConnections', connectionId);
      
      await deleteDoc(connectionRef);
      
      // Update local state
      setAddedUsers(addedUsers.filter(id => id !== userId));
    } catch (error) {
      console.error('Error removing added status:', error);
      setError('Failed to remove connection. Please try again.');
    }
  };

  const copyToClipboard = (text, event) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show brief success feedback
      const button = event?.target;
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }).catch((err) => {
      console.error('Failed to copy:', err);
    });
  };

  const getFilteredUsers = () => {
    const added = users.filter(user => addedUsers.includes(user.id));
    const notAdded = users.filter(user => !addedUsers.includes(user.id));
    return { added, notAdded };
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading social connections...</div>
        </div>
      </div>
    );
  }

  const { added, notAdded } = getFilteredUsers();

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">👥 Social</h1>
          <p className="card-subtitle">Connect with friends on Slopes and Epic</p>
        </div>

        {error && (
          <div style={{ 
            background: '#fed7d7', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #feb2b2'
          }}>
            <p style={{ margin: 0, color: '#c53030', fontSize: '0.9rem' }}>
              {error}
            </p>
          </div>
        )}

        {/* Added Friends Section */}
        {added.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              marginBottom: '15px', 
              color: 'var(--text-primary)',
              fontSize: '1.3rem',
              fontWeight: '600'
            }}>
              ✅ Added Friends ({added.length})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {added.map((user) => (
                <div
                  key={user.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '15px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                        {user.displayName}
                        {user.originalName && user.originalName !== user.displayName && (
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '0.85rem',
                            color: 'var(--text-tertiary)',
                            fontStyle: 'italic'
                          }}>
                            ({user.originalName})
                          </span>
                        )}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleRemoveAdded(user.id)}
                      style={{
                        background: '#fed7d7',
                        color: '#c53030',
                        border: '1px solid #feb2b2',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {user.slopesFriendLink && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', minWidth: '80px' }}>
                          🎿 Slopes:
                        </span>
                        <a
                          href={user.slopesFriendLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0ea5e9',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user.slopesFriendLink}
                        </a>
                        <button
                          onClick={(e) => copyToClipboard(user.slopesFriendLink, e)}
                          style={{
                            background: '#c6f6d5',
                            color: '#2f855a',
                            border: '1px solid #9ae6b4',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    )}
                    {user.epicFriendLink && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', minWidth: '80px' }}>
                          ⛷️ Epic:
                        </span>
                        <a
                          href={user.epicFriendLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0ea5e9',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user.epicFriendLink}
                        </a>
                        <button
                          onClick={(e) => copyToClipboard(user.epicFriendLink, e)}
                          style={{
                            background: '#c6f6d5',
                            color: '#2f855a',
                            border: '1px solid #9ae6b4',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not Added Friends Section */}
        <div>
          <h2 style={{ 
            marginBottom: '15px', 
            color: 'var(--text-primary)',
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            {added.length > 0 ? '➕ Add Friends' : `👥 Friends to Add (${notAdded.length})`}
          </h2>
          {notAdded.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: 'var(--text-tertiary)',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              {users.length === 0 
                ? 'No users with friend links yet. Be the first to add your links in your profile!'
                : 'All friends have been added! 🎉'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {notAdded.map((user) => (
                <div
                  key={user.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '15px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                        {user.displayName}
                        {user.originalName && user.originalName !== user.displayName && (
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '0.85rem',
                            color: 'var(--text-tertiary)',
                            fontStyle: 'italic'
                          }}>
                            ({user.originalName})
                          </span>
                        )}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleMarkAsAdded(user.id)}
                      style={{
                        background: '#c6f6d5',
                        color: '#2f855a',
                        border: '1px solid #9ae6b4',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      ✓ Mark Added
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {user.slopesFriendLink && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', minWidth: '80px' }}>
                          🎿 Slopes:
                        </span>
                        <a
                          href={user.slopesFriendLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0ea5e9',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user.slopesFriendLink}
                        </a>
                        <button
                          onClick={(e) => copyToClipboard(user.slopesFriendLink, e)}
                          style={{
                            background: '#c6f6d5',
                            color: '#2f855a',
                            border: '1px solid #9ae6b4',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    )}
                    {user.epicFriendLink && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', minWidth: '80px' }}>
                          ⛷️ Epic:
                        </span>
                        <a
                          href={user.epicFriendLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0ea5e9',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user.epicFriendLink}
                        </a>
                        <button
                          onClick={(e) => copyToClipboard(user.epicFriendLink, e)}
                          style={{
                            background: '#c6f6d5',
                            color: '#2f855a',
                            border: '1px solid #9ae6b4',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '30px' }}>
          <button
            onClick={() => navigate('/home')}
            className="btn btn-secondary"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default Social;
